"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import {
    BriefcaseIcon,
    UserGroupIcon,
    ClipboardDocumentCheckIcon,
    CheckIcon,
    Cog6ToothIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
} from "@heroicons/react/24/outline"
import { motion, AnimatePresence } from "framer-motion"

const services = [
    {
        id: "job-management",
        title: "Smart Job Management",
        short: "Post, draft, publish, or unpublish jobs easily.",
        description:
            "Employers can create job postings with specific designations and titles, save them as drafts, tag urgent hiring roles, and publish/unpublish jobs anytime.",
        features: [
            "Draft & publish control",
            "Urgent hiring tag",
            "Designation-based posting",
            "Job visibility management",
        ],
        color: "blue",
        image: "/images/services/job-management.png",
        icon: <BriefcaseIcon className="w-6 h-6" />,
    },
    {
        id: "candidate-search",
        title: "Advanced Candidate Search",
        short: "Find the right talent with detailed filters.",
        description:
            "Search and filter candidates by education, skills, designation, gender, and location. Compare results and shortlist faster.",
        features: [
            "Education & skill filters",
            "Designation & location filters",
            "Gender-based filtering",
            "Candidate comparison",
        ],
        color: "purple",
        image: "/images/services/candidate-search.png",
        icon: <UserGroupIcon className="w-6 h-6" />,
    },
    {
        id: "resume-access",
        title: "Resume & Profile Access",
        short: "Download and unlock candidate profiles.",
        description:
            "Employers can view candidate details, download resumes, and unlock premium profiles to get full access instantly.",
        features: [
            "Resume download",
            "Profile unlocking",
            "Candidate detail view",
            "Premium access",
        ],
        color: "cyan",
        image: "/images/services/resume-access.png",
        icon: <ClipboardDocumentCheckIcon className="w-6 h-6" />,
    },
    {
        id: "interview-management",
        title: "Interview Scheduling & Feedback",
        short: "Manage interviews and track candidate progress.",
        description:
            "Schedule interviews directly from the employer panel, track candidate status per job, and record structured feedback for hiring decisions.",
        features: [
            "Interview scheduling",
            "Job-wise candidate tracking",
            "Feedback management",
            "Team collaboration",
        ],
        color: "pink",
        image: "/images/services/interview-management.png",
        icon: <Cog6ToothIcon className="w-6 h-6" />,
    },
    {
        id: "subscription",
        title: "Custom Subscriptions & Top-Ups",
        short: "Flexible hiring plans for every company.",
        description:
            "Employers can create custom subscription models, add top-up credits, and manage their usage for job postings and candidate access.",
        features: [
            "Custom subscription plans",
            "Top-up credits",
            "Usage tracking",
            "Flexible billing",
        ],
        color: "orange",
        image: "/images/services/subscription.png",
        icon: <CurrencyDollarIcon className="w-6 h-6" />,
    },
]

const colorMap = {
    blue: {
        light: "bg-blue-50",
        medium: "bg-blue-100",
        dark: "bg-blue-500",
        text: "text-blue-600",
        gradient: "from-blue-400 to-blue-600",
    },
    purple: {
        light: "bg-purple-50",
        medium: "bg-purple-100",
        dark: "bg-purple-500",
        text: "text-purple-600",
        gradient: "from-purple-400 to-purple-600",
    },
    cyan: {
        light: "bg-cyan-50",
        medium: "bg-cyan-100",
        dark: "bg-cyan-500",
        text: "text-cyan-600",
        gradient: "from-cyan-400 to-cyan-600",
    },
    pink: {
        light: "bg-pink-50",
        medium: "bg-pink-100",
        dark: "bg-pink-500",
        text: "text-pink-600",
        gradient: "from-pink-400 to-pink-600",
    },
    orange: {
        light: "bg-orange-50",
        medium: "bg-orange-100",
        dark: "bg-orange-500",
        text: "text-orange-600",
        gradient: "from-orange-400 to-orange-600",
    },
    teal: {
        light: "bg-teal-50",
        medium: "bg-teal-100",
        dark: "bg-teal-500",
        text: "text-teal-600",
        gradient: "from-teal-400 to-teal-600",
    },
}

const HOVER_DELAY_MS = 100

export default function ServicesInteractivePro() {
    const [active, setActive] = useState(0)
    const [prevActive, setPrevActive] = useState(null)
    const sectionRef = useRef(null)
    const hoverTimer = useRef(null)

    const onHover = useCallback(
        (idx) => {
            clearTimeout(hoverTimer.current)
            hoverTimer.current = setTimeout(() => {
                setPrevActive((p) => (p === idx ? p : active))
                setActive(idx)
            }, HOVER_DELAY_MS)
        },
        [active]
    )

    const onLeave = useCallback(() => {
        clearTimeout(hoverTimer.current)
    }, [])

    // Preload images
    useEffect(() => {
        if (typeof window === "undefined") return
        services.forEach((s) => {
            const img = new window.Image()
            img.src = s.image
        })
    }, [])

    const activeItem = services[active]
    const prevTitle = prevActive != null ? services[prevActive]?.title : null

    return (
        <section ref={sectionRef} className="py-16 md:py-24 bg-gray-50 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-10% right--5% w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-float"></div>
            <div className="absolute bottom-10% left--5% w-60 h-60 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-float delay-1000"></div>

            <div className="px-6 md:px-12 lg:px-20 mx-auto w-full max-w-none relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-gray-800 mb-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        Our{" "}
                        <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
                            Premium
                        </span>{" "}
                        Services
                    </motion.h2>
                    <motion.p
                        className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        Comprehensive tools designed to streamline your hiring process and connect you with top
                        talent.
                    </motion.p>
                </div>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.2,
                            },
                        },
                    }}
                >
                    {/* Left: Enhanced Service List */}
                    <div className="space-y-4">
                        {services.map((item, idx) => {
                            const isActive = idx === active
                            const colors = colorMap[item.color]

                            return (
                                <motion.div
                                    key={item.id}
                                    variants={{
                                        hidden: { opacity: 0, y: 20 },
                                        visible: {
                                            opacity: 1,
                                            y: 0,
                                            transition: { duration: 0.5, ease: "easeOut" },
                                        },
                                    }}
                                    className={`
                    relative p-6 rounded-xl cursor-pointer transition-all duration-500 ease-in-out group
                    ${isActive
                                            ? `bg-white border-l-4 border-${item.color}-500 shadow-sm`
                                            : "bg-white  border border-gray-100"}
                  `}
                                    onMouseEnter={() => onHover(idx)}
                                    onMouseLeave={onLeave}
                                    onClick={() => setActive(idx)}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                                    }}
                                >
                                    <div
                                        className={`absolute inset-0 rounded-xl bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out ${colors.light}`}
                                    />

                                    <div className="relative z-10 flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div
                                                className={`
                          flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-500 ease-in-out
                          ${isActive
                                                        ? `bg-gradient-to-r ${colors.gradient} text-white shadow-sm`
                                                        : `${colors.light} ${colors.text}`}
                        `}
                                            >
                                                {item.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h3
                                                    className={`text-lg font-semibold transition-colors duration-300 ease-in-out ${isActive ? `${colors.text}` : "text-gray-800 group-hover:text-gray-900"
                                                        }`}
                                                >
                                                    {item.title}
                                                </h3>
                                                {isActive && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -5 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="text-gray-600 mt-2 text-sm leading-relaxed"
                                                    >
                                                        {item.short}
                                                    </motion.div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>

                    {/* Right: Enhanced Preview Card */}
                    <div className="sticky top-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeItem.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                                className="rounded-xl p-8 relative overflow-hidden bg-white shadow-sm border border-gray-100"
                                whileHover={{
                                    y: -5,
                                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                                }}
                            >
                                <div
                                    className={`absolute -top-24 -right-24 w-48 h-48 rounded-full ${colorMap[activeItem.color].medium} opacity-10`}
                                />

                                <div className="relative z-10">
                                    {prevTitle && (
                                        <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
                                            <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium">
                                                Previous
                                            </span>
                                            <span className="truncate">{prevTitle}</span>
                                        </div>
                                    )}

                                    <div className="flex flex-col lg:flex-row gap-8 items-start">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div
                                                    className={`p-3 rounded-xl bg-gradient-to-r ${colorMap[activeItem.color].gradient} text-white shadow-lg`}
                                                >
                                                    {activeItem.icon}
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-900">{activeItem.title}</h3>
                                            </div>

                                            <p className="text-gray-600 mb-6 leading-relaxed">
                                                {activeItem.description}
                                            </p>

                                            <div className="space-y-3">
                                                {activeItem.features.map((feature) => (
                                                    <motion.div
                                                        key={feature}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="flex items-center gap-3"
                                                    >
                                                        <div
                                                            className={`p-1 rounded-full ${colorMap[activeItem.color].text}`}
                                                        >
                                                            <CheckIcon className="w-5 h-5" />
                                                        </div>
                                                        <span className="text-gray-700 font-medium">{feature}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <div className="text-center mt-4 text-sm text-gray-500">
                            Click or hover over services to explore
                        </div>
                    </div>
                </motion.div>
            </div>

            <style jsx>{`
        .bg-gradient-to-r {
          background-image: linear-gradient(to right, var(--tw-gradient-stops));
        }

        .text-transparent {
          color: transparent;
        }

        .bg-clip-text {
          -webkit-background-clip: text;
          background-clip: text;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .delay-1000 {
          animation-delay: 1s;
        }

        /* Custom positioning utilities */
        .top-10% {
          top: 10%;
        }

        .right--5% {
          right: -5%;
        }

        .bottom-10% {
          bottom: 10%;
        }

        .left--5% {
          left: -5%;
        }
      `}</style>
        </section>
    )
}